import {
  ChangeEvent,
  ClipboardEvent,
  CSSProperties,
  memo,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useTranslation } from 'react-i18next'
import { FixedSizeList as List } from 'react-window'

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import CloseIcon from '@mui/icons-material/Close'
import { BackdropProps, MenuList, PopoverPosition, Tooltip } from '@mui/material'
import { FieldInputProps, useField } from 'formik'
import { parsePhoneNumberFromString, PhoneNumber } from 'libphonenumber-js'

import { FieldSettings, FormHelpersProviderReturn } from '../../../ana-toolbox'
import { SupportedLocale } from '../../utils/helpers/date'
import { getHasError } from '../../utils/helpers/getHasError'
import { getPhoneMetadata, metadataByCountryCode } from '../../utils/helpers/phoneMetadata'
import { useFormHelpers } from '../../utils/hooks/useFormHelpers'
import { useSubmitCount } from '../../utils/hooks/useSubmitCount'
import { TextFieldLabel } from '../../utils/shared/TextFieldLabel'
import { combinedFields } from '../../utils/yup/main'
import { FieldHelperText } from '../field-helper-text/field-helper-text'
import { TextInput } from '../text-input/text-input'
import { Translate } from '../translate/translate'

import * as S from './phone-field.styles'

const removeCountryCode = (countryCode: string | number, phoneNumber: string) =>
  phoneNumber.replace(`+${countryCode}`, '').trim().replace('+', '')

export type PhoneFieldProps = {
  className?: string
  error?: string
  name: string
  label: ReactNode
  language?: SupportedLocale
  required?: boolean
  flagSource: (country: string) => string
  helperText?: ReactNode
  onChange: (e: ChangeEvent<HTMLInputElement>) => void
  onBlur: (e: FocusEvent) => void
}

const PhoneFieldBase = memo(
  ({
    value,
    error,
    name,
    language = 'de',
    helperText,
    flagSource,
    required = false,
    label,
    onChange,
    onBlur,
    ...props
  }: PhoneFieldProps & FieldInputProps<string>) => {
    const { t, i18n } = useTranslation()

    const [anchorPosition, setAnchorPosition] = useState<null | PopoverPosition>(null)
    const [menuWidth, setMenuWidth] = useState<string | number>('auto')
    const [searchTerm, setSearchTerm] = useState<string>('')
    const inputRef = useRef<HTMLInputElement | null>(null)

    const countriesForCurrentLanguage = useMemo(() => getPhoneMetadata(language as SupportedLocale), [language])

    const countriesFiltered = useMemo(
      () =>
        countriesForCurrentLanguage.filter((country) =>
          country.name[language as SupportedLocale].toLowerCase().includes(searchTerm.toLowerCase())
        ),
      [countriesForCurrentLanguage, language, searchTerm]
    )

    const [currentCountryCode, setCurrentCountryCode] = useState(() => {
      try {
        const { country } = parsePhoneNumberFromString(value) as PhoneNumber

        return country || 'de'
      } catch {
        return 'de'
      }
    })

    const open = Boolean(anchorPosition)

    const currentCountry = useMemo(() => {
      return metadataByCountryCode[currentCountryCode.toUpperCase() as keyof typeof metadataByCountryCode]
    }, [currentCountryCode])

    /* Removes the country code from the value */
    const displayValue = useMemo(
      () => (value ? removeCountryCode(currentCountry?.callingCode, value) : undefined),
      [value, currentCountry?.callingCode]
    )

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      const parentElement = event.currentTarget.parentElement

      if (parentElement) {
        setAnchorPosition({
          top: parentElement.offsetTop + 48,
          left: parentElement.offsetLeft,
        })

        setMenuWidth(event.currentTarget.parentElement?.clientWidth || 'auto')
      }
    }

    const handleSearchTermChange = (event: ChangeEvent<HTMLInputElement>) => {
      event.preventDefault()
      event.stopPropagation()

      setSearchTerm(event.target.value)
    }

    const handleClearSearch = () => setSearchTerm('')

    const handleCountryClick = (country: string) => () => {
      setCurrentCountryCode(country.toLowerCase())
      setAnchorPosition(null)
      handleClearSearch()

      onChange({
        target: {
          name: name,
          value: '',
        },
      })
    }

    const handleClose = () => {
      setAnchorPosition(null)
    }

    const handleBlur = (e: React.FocusEvent) => {
      onBlur(e)
    }

    const flagLabel = useMemo(() => {
      const countryName =
        metadataByCountryCode[currentCountryCode.toUpperCase() as keyof typeof metadataByCountryCode]?.name[
          i18n.language as 'en' | 'de'
        ]

      return `${countryName || 'Unknown'} flag`
    }, [i18n.language, currentCountryCode])

    const renderCountry = ({ index, style }: { index: number; style: CSSProperties }) => {
      const country = countriesFiltered[index]

      return (
        <S.MenuItem
          key={country.code}
          onClick={handleCountryClick(country.iso2code)}
          selected={country.iso2code === currentCountry.iso2code}
          data-testid="menu-item"
          style={style}
        >
          <S.Flag image={flagSource(country.iso2code.toLowerCase())} />

          <S.MenuItemText>
            {country.name[i18n.language as 'en' | 'de']} +{country?.callingCode}
          </S.MenuItemText>
        </S.MenuItem>
      )
    }

    const MemoTextFieldLabel = memo(TextFieldLabel)

    return (
      <>
        <S.Container>
          <S.Button type="button" onClick={handleClick} name="country-flag" aria-label="Select country">
            <S.Flag image={flagSource(currentCountryCode)} aria-label={flagLabel} />
            <ArrowDropDownIcon />
          </S.Button>

          {open && (
            <S.Popover
              disableAutoFocus
              anchorPosition={anchorPosition ?? undefined}
              disablePortal
              BackdropProps={
                {
                  open: open,
                  'data-testid': 'countries-backdrop',
                  style: { backgroundColor: 'transparent' },
                } as BackdropProps
              }
              keepMounted
              open={open}
              onClose={handleClose}
              anchorReference="anchorPosition"
              PaperProps={{
                style: { width: menuWidth },
              }}
            >
              <TextInput
                name="countriesSearchTerm"
                label={t('countries_search')}
                value={searchTerm}
                onChange={handleSearchTermChange}
                endAdornment={
                  searchTerm.length > 0 ? (
                    <Tooltip title={t('clear').toString()}>
                      <S.SearchClearButton name="clearSearch" onClick={handleClearSearch}>
                        <CloseIcon />
                      </S.SearchClearButton>
                    </Tooltip>
                  ) : undefined
                }
              />

              <MenuList>
                <List height={248} itemCount={countriesFiltered.length} itemSize={48} width={300}>
                  {renderCountry}
                </List>
              </MenuList>
            </S.Popover>
          )}

          <S.TextField
            label={
              <MemoTextFieldLabel required={required}>
                <Translate>{label}</Translate>
              </MemoTextFieldLabel>
            }
            InputProps={{
              startAdornment: <S.CallingCode>+{currentCountry?.callingCode}</S.CallingCode>,
              inputRef,
            }}
            InputLabelProps={{
              id: `${name}-label`,
            }}
            inputProps={{
              'aria-labelledby': `${name}-label`,
            }}
            value={displayValue || ''}
            fullWidth
            error={Boolean(error)}
            type="tel"
            name={name}
            onBlur={handleBlur}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              onChange({
                target: {
                  name: name,
                  value: `+${currentCountry.callingCode}${removeCountryCode(currentCountryCode, e.target.value)}`,
                },
              })
            }}
            onPaste={(event: ClipboardEvent) => {
              event.preventDefault()

              const clipboardData = event.clipboardData
              const pastedData = clipboardData?.getData('text')

              const onInvalidPastedData = () => {
                onChange({
                  target: {
                    name: name,
                    value: '',
                  },
                })

                setCurrentCountryCode('de')
              }

              if (typeof pastedData !== 'string') {
                return onInvalidPastedData()
              }

              try {
                const { number, country } = parsePhoneNumberFromString(pastedData) as PhoneNumber

                if (number && country) {
                  onChange({
                    target: {
                      name: name,
                      value: number,
                    },
                  })

                  setCurrentCountryCode(country.toLowerCase())
                }
              } catch {
                return onInvalidPastedData()
              }
            }}
            {...props}
          />
        </S.Container>

        <FieldHelperText helperText={helperText} error={error} name={name} />
      </>
    )
  }
)

PhoneFieldBase.displayName = 'PhoneFieldBase'

export const PhoneField = (props: PhoneFieldProps) => {
  const submitCount = useSubmitCount()
  const formHelpers = useFormHelpers()
  const [field, meta] = useField(props.name)

  const hasError = useMemo(() => getHasError(meta, submitCount ?? 1), [meta, submitCount])

  const fieldSettings = useMemo(
    () => combinedFields[props.name as keyof typeof combinedFields] as FieldSettings,
    [props.name]
  )

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      fieldSettings?.hooks?.beforeChange?.(e.target.value, formHelpers as FormHelpersProviderReturn, e)
      field.onChange(e)
    },
    // Ignoring these dependencies to prevent unnecessary re renderes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return <PhoneFieldBase error={hasError ? meta.error : undefined} {...props} {...field} onChange={handleChange} />
}

PhoneField.displayName = 'PhoneField'
